// Results arrive on later frames. Never wait for the GPU or grow the query
// pool when it falls behind. TIME_ELAPSED_EXT reports nanoseconds.
export class GpuTimer {
    constructor(gl, onSample, onInvalid) {
        this.gl = gl;
        this.onSample = onSample;
        this.onInvalid = onInvalid;
        this.extension = gl.getExtension('EXT_disjoint_timer_query_webgl2');
        // Firefox can expose the legacy extension even on a WebGL2 context.
        // Its query objects must be used through EXT methods, not core methods.
        const legacy = this.extension ? null : gl.getExtension('EXT_disjoint_timer_query');
        this.extension ??= legacy;
        const ext = this.extension;
        this.api = legacy ? {
            bits: () => ext.getQueryEXT(ext.TIME_ELAPSED_EXT, ext.QUERY_COUNTER_BITS_EXT),
            create: () => ext.createQueryEXT(), delete: query => ext.deleteQueryEXT(query),
            begin: query => ext.beginQueryEXT(ext.TIME_ELAPSED_EXT, query), end: () => ext.endQueryEXT(ext.TIME_ELAPSED_EXT),
            available: query => ext.getQueryObjectEXT(query, ext.QUERY_RESULT_AVAILABLE_EXT),
            result: query => ext.getQueryObjectEXT(query, ext.QUERY_RESULT_EXT),
        } : ext ? {
            bits: () => gl.getQuery(ext.TIME_ELAPSED_EXT, ext.QUERY_COUNTER_BITS_EXT),
            create: () => gl.createQuery(), delete: query => gl.deleteQuery(query),
            begin: query => gl.beginQuery(ext.TIME_ELAPSED_EXT, query), end: () => gl.endQuery(ext.TIME_ELAPSED_EXT),
            available: query => gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE),
            result: query => gl.getQueryParameter(query, gl.QUERY_RESULT),
        } : null;
        this.supported = !!this.api && this.api.bits() > 0;
        this.unavailableReason = !ext ? 'browser does not expose GPU timer queries' : !this.supported ? 'GPU elapsed-time counter is unavailable' : null;
        this.measurement = 'execution';
        // Firefox/driver/privacy configurations may omit both timer extensions.
        // WebGL2 fences still expose asynchronous completion. This is an upper
        // bound (includes command queuing and polling), never GPU utilization.
        if (!this.supported && gl.fenceSync && gl.clientWaitSync && gl.deleteSync) {
            this.measurement = 'completion'; this.supported = true;
            let active;
            this.api = {
                create: () => ({}),
                delete: query => { if (query.sync) gl.deleteSync(query.sync); },
                begin: query => { active = query; if (query.sync) gl.deleteSync(query.sync); query.sync = null; query.start = performance.now(); },
                end: () => { active.sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0); gl.flush(); },
                available: query => {
                    if (!query.sync) return false;
                    const result = gl.clientWaitSync(query.sync, 0, 0);
                    query.failed = result === gl.WAIT_FAILED;
                    return query.failed || result === gl.ALREADY_SIGNALED || result === gl.CONDITION_SATISFIED;
                },
                result: query => query.failed ? NaN : (performance.now() - query.start) * 1e6,
            };
        }
        this.queries = [];
        this.active = null;
    }
    begin(now) {
        const gl = this.gl, ext = this.extension;
        if (!this.supported || this.active || gl.isContextLost()) return;
        if (this.measurement === 'execution' && gl.getParameter(ext.GPU_DISJOINT_EXT)) {
            this.reset(); this.onInvalid(); return;
        }
        for (const entry of this.queries) {
            if (!entry.pending || !this.api.available(entry.query)) continue;
            const ms = this.api.result(entry.query) / 1e6;
            entry.pending = false;
            if (Number.isFinite(ms) && ms >= 0) this.onSample(entry.now, ms);
        }
        let entry = this.queries.find(entry => !entry.pending);
        if (!entry) {
            if (this.queries.length >= 8) return;
            const query = this.api.create();
            if (!query) return;
            entry = { query, pending: false, now: 0 }; this.queries.push(entry);
        }
        entry.now = now;
        this.api.begin(entry.query);
        this.active = entry;
    }
    end() {
        if (!this.active) return;
        if (!this.gl.isContextLost()) this.api.end();
        this.active.pending = true; this.active = null;
    }
    reset() {
        this.end();
        if (!this.gl.isContextLost()) for (const entry of this.queries) this.api.delete(entry.query);
        this.queries.length = 0;
    }
}
